import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { filterTools, slugify, suggestSlug, defaultScopeKeys, filterSlugInput, sameScopeKeys, connectionLabel, suggestedToConnect, matchesFilter, catalogTiles, addableProviders } from "../connections";
import { api } from "../api";
import { useDropdown } from "../hooks";
import { ConfirmDialog, ConnIcon, DocsLink, ErrorBanner, Skeleton, Snippet } from "../components";

// Connections: the accounts, secrets and tool servers an owner attaches once and
// can then grant to their apps. One gallery of logo tiles, not three lists,
// because a person recognizes a logo before a label; the kind (an account you
// SIGN IN to, a pasted key you STORE, an MCP server that is a set of TOOLS)
// is the tile's subtitle. One "Add connection" menu in the corner offers the
// catalog in those three sections. While the gallery is empty it shows the
// catalog as dashed tiles instead, and the operator's suggested ones stay
// dashed until connected.
//
// The credential itself is never shown or returned. This page says WHAT is
// attached; each app's settings say which of them that app may use.
const Connections = () => {
  const [data, setData] = useState(null);
  const [defs, setDefs] = useState(null);
  const [editingProvider, setEditingProvider] = useState(null);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(null);
  const [managing, setManaging] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    try {
      const [conns, providers] = await Promise.all([
        api.get("/api/connections"),
        api.get("/api/providers"),
      ]);
      setData(conns);
      setDefs(providers);
    } catch (err) {
      setError(err.message);
      setData({ connections: [], providers: [] });
      setDefs({ providers: [], redirect_uri: "" });
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const providers = data?.providers || [];
  const mcpProvider = providers.find((p) => p.kind === "mcp") || null;

  // Consents here leave for the provider and come back to this page (the
  // popup flow is the welcome tour's, where leaving would lose the tour).
  const remove = async (c) => {
    setError("");
    setBusy(true);
    try {
      await api.del(`/api/connections/${encodeURIComponent(c.slug)}`);
      setRemoving(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // What the gallery shows besides your connections: with nothing connected,
  // everything this instance can connect; otherwise the suggested services you
  // have not connected yet. Everything else is in the Add connection menu.
  const conns = data?.connections || [];
  const dashed = suggestedToConnect(data || {}, { includeConnected: false });
  const catalog = conns.length === 0 && data ? catalogTiles(data) : dashed;
  const filtered = conns.filter((c) => matchesFilter(c, filter, query));
  const withFilters = conns.length > 4;

  return (
    <>
      <div className="page-header conn-header">
        <div>
          <h1>Connections</h1>
          <p className="hint conn-lead">
            Everything your apps and chats may use, at a glance. Starred ones go to every new chat and
            app; a grant is per app and revocable.{" "}
            <DocsLink guide="user" section="connections">How connections work</DocsLink>
          </p>
        </div>
        <AddConnectionMenu
          providers={addableProviders(providers)}
          presets={data?.mcp_servers || []}
          onPick={setAdding}
          onAddOwn={() => setEditingProvider({})}
        />
      </div>
      <ErrorBanner message={error} onDismiss={() => setError("")} />
      {withFilters && (
        <div className="conn-filters">
          {[["all", "All"], ["oauth", "Accounts"], ["static", "Credentials"], ["mcp", "MCP servers"], ["default", "By default"], ["attention", "Needs attention"]].map(([key, label]) => (
            <button key={key} type="button" className={"conn-chip" + (filter === key ? " on" : "")} onClick={() => setFilter(key)} aria-pressed={filter === key}>
              {label}
            </button>
          ))}
          <input type="text" className="conn-filter-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search..." aria-label="Search connections" />
        </div>
      )}
      {data === null && !error && <Skeleton rows={3} card label="Loading connections..." />}
      {data !== null && (
        <div className="conn-gallery">
          {filtered.map((c) => (
            <ConnectionTile key={c.slug} conn={c} onManage={setManaging} />
          ))}
          {filter === "all" && !query && catalog.map((p) => (
            <CatalogTile key={(p.kind === "mcp" ? "mcp:" : "") + p.name} item={p} onPick={() => setAdding(p.kind === "mcp" ? { ...mcpProvider, preset: p } : p)} />
          ))}
        </div>
      )}

      {adding && (
        <AddConnectionDialog
          provider={adding}
          existing={data?.connections || []}
          onClose={() => setAdding(null)}
          onAdded={async () => {
            setAdding(null);
            await load();
          }}
        />
      )}
      {defs && <MyProvidersCard defs={defs} onEdit={setEditingProvider} onChanged={load} onError={setError} />}

      {editingProvider && (
        <ProviderDialog
          existing={editingProvider.name ? editingProvider : null}
          redirectURI={defs?.redirect_uri || ""}
          onClose={() => setEditingProvider(null)}
          onSaved={async () => {
            setEditingProvider(null);
            await load();
          }}
        />
      )}
      {managing && (
        <ManageConnectionModal
          conn={managing}
          provider={providers.find((p) => p.name === managing.provider)}
          onChanged={load}
          onClose={() => setManaging(null)}
          onRemove={setRemoving}
        />
      )}
      {removing && (
        <ConfirmDialog
          title={`Remove ${removing.label || removing.slug}?`}
          confirmLabel="Remove"
          busy={busy}
          onClose={() => setRemoving(null)}
          onConfirm={() => remove(removing)}
          body={
            <>
              The stored credential is deleted and cannot be recovered.{" "}
              {removing.granted_apps > 0 ? (
                <>
                  <b>
                    {removing.granted_apps} app{removing.granted_apps === 1 ? "" : "s"}
                  </b>{" "}
                  {removing.granted_apps === 1 ? "loses" : "lose"} access immediately.
                </>
              ) : (
                <>No app is using it.</>
              )}
              {removing.kind === "oauth" && (
                <> Reconnecting later means approving it at the provider again.</>
              )}
            </>
          }
        />
      )}
    </>
  );
};

// The user's OWN provider definitions -- services hostit does not ship and the
// operator has not set up, which they registered an OAuth app with themselves.
//
// Shown only once they have one. An empty card explaining a concept nobody has
// used yet is noise on a page that already has three; the way in is the line
// under the Accounts card.
const MyProvidersCard = ({ defs, onEdit, onChanged, onError }) => {
  const [removing, setRemoving] = useState(null);
  const [busy, setBusy] = useState(false);
  const mine = (defs.providers || []).filter((p) => p.scope === "personal" && p.editable && p.kind !== "mcp");
  if (mine.length === 0) return null;

  const remove = async (p) => {
    setBusy(true);
    onError("");
    try {
      await api.del(`/api/providers/${encodeURIComponent(p.name)}`);
      setRemoving(null);
      await onChanged();
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="conn-head">
        <h2>Your own services</h2>
        <button type="button" className="btn btn-primary btn-small" onClick={() => onEdit({})}>
          Add service
        </button>
      </div>
      <p className="hint">
        Services you registered an OAuth app with yourself. Only you can see or use these; they
        appear in your <b>Add account</b> menu alongside the ones hostit ships.{" "}
        <DocsLink guide="user" section="connections" sub="own">How this works</DocsLink>
      </p>
      {mine.map((p) => (
        <div key={p.name} className="conn-row">
          <div className="conn-id">
            <span className="conn-name">
              {p.label}
              <span className="conn-provider">OAuth</span>
            </span>
            <span className="conn-note">
              connect as <span className="mono">{p.name}</span>
              {" -- "}client <span className="mono">{p.client_id}</span>
            </span>
          </div>
          <div className="menu conn-rowmenu">
            <button type="button" className="btn btn-small" onClick={() => onEdit(p)}>Edit</button>
            <button type="button" className="btn btn-small" onClick={() => setRemoving(p)}>Remove</button>
          </div>
        </div>
      ))}
      {removing && (
        <ConfirmDialog
          title={`Remove ${removing.label}?`}
          confirmLabel="Remove"
          busy={busy}
          onClose={() => setRemoving(null)}
          onConfirm={() => remove(removing)}
          body={
            <>
              The definition goes; accounts already connected through it keep working until their
              token expires and then cannot be refreshed. Your OAuth app at the service itself is
              untouched.
            </>
          }
        />
      )}
    </div>
  );
};

// Defining a service. The redirect URI is shown first and copyable, because it
// is the one value the person cannot work out and the one that fails the whole
// flow at the vendor if it is wrong.
const ProviderDialog = ({ existing, redirectURI, onClose, onSaved }) => {
  const [label, setLabel] = useState(existing?.label || "");
  const [name, setName] = useState(existing?.name || "");
  const [nameEdited, setNameEdited] = useState(Boolean(existing));
  const [form, setForm] = useState({
    client_id: existing?.client_id || "",
    client_secret: "",
    auth_url: existing?.auth_url || "",
    token_url: existing?.token_url || "",
    issuer: existing?.issuer || "",
    scopes: (existing?.scopes || []).join(" "),
  });
  const [shortDescription, setShortDescription] = useState(existing?.short_description || "");
  const [longDescription, setLongDescription] = useState(existing?.long_description || "");
  const [userToken, setUserToken] = useState(Boolean(existing?.user_token));
  const [allowMultiple, setAllowMultiple] = useState(existing?.allow_multiple !== false);
  const [scopeOptions, setScopeOptions] = useState(() =>
    (existing?.scope_options || []).map((o) => ({
      key: o.key || "", label: o.label || "", help: o.help || "",
      scopes: (o.scopes || []).join(" "), default: Boolean(o.default),
    })),
  );
  const setOption = (i, k, v) => setScopeOptions((os) => os.map((o, j) => (j === i ? { ...o, [k]: v } : o)));
  const addOption = () => setScopeOptions((os) => [...os, { key: "", label: "", help: "", scopes: "", default: false }]);
  const removeOption = (i) => setScopeOptions((os) => os.filter((_, j) => j !== i));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const reference = nameEdited ? name : slugify(label);
  const endpointsOK = form.issuer.trim() || (form.auth_url.trim() && form.token_url.trim());
  const valid =
    label.trim() && reference.length >= 3 && endpointsOK &&
    form.client_id.trim() && (existing || form.client_secret.trim());

  const submit = async (e) => {
    e.preventDefault();
    if (busy || !valid) return;
    setBusy(true);
    setError("");
    const body = {
      name: reference, label: label.trim(), kind: "oauth",
      client_id: form.client_id.trim(),
      client_secret: form.client_secret.trim(),
      auth_url: form.auth_url.trim(),
      token_url: form.token_url.trim(),
      issuer: form.issuer.trim(),
      scopes: form.scopes.split(/\s+/).filter(Boolean),
      user_token: userToken,
      short_description: shortDescription.trim(),
      long_description: longDescription.trim(),
      allow_multiple: allowMultiple,
      scope_options: scopeOptions
        .map((o) => ({
          key: o.key.trim(), label: o.label.trim(), help: o.help.trim(),
          scopes: o.scopes.split(/\s+/).filter(Boolean), default: o.default,
        }))
        .filter((o) => o.key && o.scopes.length > 0),
    };
    try {
      if (existing) {
        await api.put(`/api/providers/${encodeURIComponent(existing.name)}`, body);
      } else {
        await api.post("/api/providers", body);
      }
      onSaved();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal wider onClose={onClose} title={existing ? `Edit ${existing.label}` : "Add your own service"}>
      <form onSubmit={submit}>
        <ErrorBanner message={error} onDismiss={() => setError("")} />
        <p className="hint">
          For an OAuth service users sign in to. Register an app with it first, giving it this
          callback URL:
        </p>
        <Snippet text={redirectURI} />
        <div className="conn-grid">
          <label className="conn-field">
            <span>Name <RequiredStar /></span>
            <input type="text" value={label} onChange={(e) => setLabel(e.target.value)}
              placeholder="Acme" aria-label="Service name" autoFocus disabled={busy} />
          </label>
          <label className="conn-field">
            <span>Reference</span>
            <input type="text" className="mono" value={reference}
              onChange={(e) => { setNameEdited(true); setName(e.target.value); }}
              aria-label="Reference" disabled={busy || Boolean(existing)} />
          </label>
          <label className="conn-field">
            <span>Client ID <RequiredStar /></span>
            <input type="text" className="mono" value={form.client_id} onChange={set("client_id")}
              aria-label="Client ID" disabled={busy} />
          </label>
          <label className="conn-field">
            <span>Client secret {existing ? <span className="conn-field-note">(leave blank to keep)</span> : <RequiredStar />}</span>
            <input type="password" value={form.client_secret} onChange={set("client_secret")}
              aria-label="Client secret" disabled={busy} />
          </label>
          <label className="conn-field">
            <span>Authorize URL <RequiredStar /></span>
            <input type="text" className="mono" value={form.auth_url} onChange={set("auth_url")}
              placeholder="https://acme.example.com/oauth/authorize" aria-label="Authorize URL" disabled={busy} />
          </label>
          <label className="conn-field">
            <span>Token URL <RequiredStar /></span>
            <input type="text" className="mono" value={form.token_url} onChange={set("token_url")}
              placeholder="https://acme.example.com/oauth/token" aria-label="Token URL" disabled={busy} />
          </label>
          <label className="conn-field conn-field-wide">
            <span>Issuer <span className="conn-field-note">(optional &mdash; discovers the two URLs above for you)</span></span>
            <input type="text" className="mono" value={form.issuer} onChange={set("issuer")}
              placeholder="https://acme.example.com" aria-label="Issuer" disabled={busy} />
          </label>
        </div>

        <details className="conn-advanced">
          <summary>Advanced (optional)</summary>
          <label className="conn-field">
            <span>Scopes <span className="conn-field-note">(space-separated baseline)</span></span>
            <input type="text" className="mono" value={form.scopes} onChange={set("scopes")}
              placeholder="read write" aria-label="Scopes" disabled={busy} />
          </label>
          <label className="conn-field">
            <span>Short description <span className="conn-field-note">(shown in the add dialog)</span></span>
            <input type="text" value={shortDescription} onChange={(e) => setShortDescription(e.target.value)}
              placeholder="What this connection is" aria-label="Short description" disabled={busy} />
          </label>
          <label className="conn-field">
            <span>Long description <span className="conn-field-note">(documents the API for a granted app&rsquo;s assistant)</span></span>
            <textarea rows={3} className="conn-textarea" value={longDescription} onChange={(e) => setLongDescription(e.target.value)}
              placeholder="Base URL, auth header, key endpoints" aria-label="Long description" disabled={busy} />
          </label>
          <label className="conn-check">
            <input type="checkbox" checked={userToken} onChange={(e) => setUserToken(e.target.checked)} disabled={busy} />
            <span>User token (Slack) &mdash; the token acts as the person, not a bot</span>
          </label>
          <label className="conn-check">
            <input type="checkbox" checked={allowMultiple} onChange={(e) => setAllowMultiple(e.target.checked)} disabled={busy} />
            <span>Allow more than one connection to this app</span>
          </label>

        <fieldset className="conn-scopes-editor">
          <legend>Permission checkboxes</legend>
          <p className="hint">
            Each becomes a checkbox in the add dialog: a labelled bundle of scopes the user grants or withholds.
            Leave empty to request the scopes above exactly.
          </p>
          {scopeOptions.map((o, i) => (
            <div key={i} className="conn-option-row">
              <input type="text" className="mono" value={o.key} onChange={(e) => setOption(i, "key", e.target.value)}
                placeholder="key" aria-label="Option key" disabled={busy} />
              <input type="text" value={o.label} onChange={(e) => setOption(i, "label", e.target.value)}
                placeholder="Label" aria-label="Option label" disabled={busy} />
              <input type="text" className="mono" value={o.scopes} onChange={(e) => setOption(i, "scopes", e.target.value)}
                placeholder="scope:a scope:b" aria-label="Option scopes" disabled={busy} />
              <label className="conn-option-default" title="Ticked by default">
                <input type="checkbox" checked={o.default} onChange={(e) => setOption(i, "default", e.target.checked)} disabled={busy} />
                <span>default</span>
              </label>
              <button type="button" className="btn btn-small" onClick={() => removeOption(i)} disabled={busy} aria-label="Remove option">Remove</button>
            </div>
          ))}
          <button type="button" className="btn btn-small" onClick={addOption} disabled={busy}>Add permission</button>
        </fieldset>
        </details>

        <div className="btn-row">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy || !valid}>
            {busy ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// The apps that hold a connection. Live apps are named and linked; chats and
// archived apps are collapsed into clickable counts (a chat's id is noise, and
// archived apps are not front-of-mind), all of which open a modal with the full,
// named list. Capped so a widely shared connection names the first few apps and
// then counts the rest rather than turning one row into a wall of links.
const grantedAppsCap = 5;

// A conversation's label in the grants modal: its title, else a short fallback.
const grantLabel = (g) => (g.kind === "chat" ? g.title || "New chat" : g.name);

// Where a granted app or chat links to: a chat to its page, an app to its
// connections tab.
const grantHref = (g) => (g.kind === "chat" ? `/chats/${g.name}` : `/app/${g.name}/connections`);

const GrantsModal = ({ conn, apps, chats, archived, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const Section = ({ title, items }) =>
    items.length === 0 ? null : (
      <div className="grants-section">
        <h3>{title}</h3>
        <ul className="grants-list">
          {items.map((g) => (
            <li key={g.name}>
              <Link to={grantHref(g)} onClick={onClose}>{grantLabel(g)}</Link>
            </li>
          ))}
        </ul>
      </div>
    );
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onMouseDown={onClose}>
      <div className="card modal modal-sheet" onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="modal-x" onClick={onClose} title="Close" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
        <h2>Granted to</h2>
        <p className="hint">Everything that can use {conn.label || conn.slug}.</p>
        <Section title="Apps" items={apps} />
        <Section title={`Conversations (${chats.length})`} items={chats} />
        <Section title={`Archived apps (${archived.length})`} items={archived} />
      </div>
    </div>
  );
};

const GrantedApps = ({ conn }) => {
  const [open, setOpen] = useState(false);
  const list = conn.granted_app_list || [];
  if (list.length === 0) return <em className="conn-ungranted">Not granted to any app yet</em>;
  const apps = list.filter((g) => g.kind !== "chat" && !g.archived);
  const chats = list.filter((g) => g.kind === "chat" && !g.archived);
  const archived = list.filter((g) => g.archived);
  const shownApps = apps.slice(0, grantedAppsCap);
  const restApps = apps.length - shownApps.length;
  const chip = (label, key) => (
    <button key={key} type="button" className="grants-chip" onClick={() => setOpen(true)}>
      {label}
    </button>
  );
  // Assemble the comma-separated parts: named live apps, then a chats count, an
  // archived count, and an "N more apps" chip -- each collapsed part opens the
  // modal with the full named list.
  const parts = [];
  shownApps.forEach((g) => parts.push(<Link key={g.name} to={grantHref(g)}>{g.name}</Link>));
  if (restApps > 0) parts.push(chip(`${restApps} more app${restApps === 1 ? "" : "s"}`, "more"));
  if (chats.length > 0) parts.push(chip(`${chats.length} chat${chats.length === 1 ? "" : "s"}`, "chats"));
  if (archived.length > 0) parts.push(chip(`${archived.length} archived app${archived.length === 1 ? "" : "s"}`, "archived"));
  return (
    <>
      granted to{" "}
      {parts.map((p, i) => (
        <span key={i}>
          {i > 0 ? ", " : ""}
          {p}
        </span>
      ))}
      {open && (
        <GrantsModal conn={conn} apps={apps} chats={chats} archived={archived} onClose={() => setOpen(false)} />
      )}
    </>
  );
};

// What a tile calls each kind of connection.
const kindNames = { oauth: "Account", static: "Credential", mcp: "MCP server", delegated: "Service" };

// A connected service, as a tile: the logo, the name and provider, the default
// star, health, who holds the grant, and the row menu with every action. The
// same information as a row, laid out to scan by logo.
// The health dot's colour: green when ok, amber for a delegated connection
// still waiting on access, red for one the provider rejected (reconnect/admin).
const dotClass = (status) =>
  status === "needs_reconnect" || status === "needs_admin" ? " danger" : status === "no_access" || status === "unreachable" ? " warn" : "";

const ConnectionTile = ({ conn: c, onManage }) => {
  // The quiet face: a big borderless logo, the name with a health dot, and its
  // kind with the default badge. The whole card opens the manage modal, where
  // everything -- edit, reconnect, the default toggle, the tools -- lives.
  const warn = c.status === "needs_reconnect" || c.status === "needs_admin" || c.status === "no_access" || c.status === "unreachable";
  // A server hostit calls tools on is an MCP server, whether a user added it or
  // it is a delegated one the operator configured.
  const kindLabel = c.url ? "MCP server" : kindNames[c.kind] || "";
  return (
    <button type="button" className={"conn-tile" + (warn ? " conn-tile-warn" : "")} onClick={() => onManage(c)}>
      <span className="conn-tile-logo"><ConnIcon url={c.icon_url} size={46} /></span>
      <div className="conn-tile-name">
        {c.label || c.slug}
        <span className={"conn-tile-dot" + dotClass(c.status)} title={warn ? "Needs attention" : "Connected"} aria-label={warn ? "Needs attention" : "Connected"} />
      </div>
      <div className="conn-tile-kind">
        {kindLabel}
        {c.default_grant && (
          <span className="conn-default-badge" title="Granted to every new chat and app you create">default</span>
        )}
      </div>
    </button>
  );
};

// A service you could connect but have not: a dashed tile that opens the add
// dialog for it. The empty gallery is made of these; a filled one shows only
// the operator's suggestions.
const CatalogTile = ({ item, onPick }) => (
  <button type="button" className="conn-tile conn-tile-add" onClick={onPick} title={item.help || item.short_description || ""}>
    <span className="conn-tile-logo"><ConnIcon url={item.icon_url} size={46} /></span>
    <span className="conn-tile-name">{item.label}</span>
    <span className="conn-tile-kind">{kindNames[item.kind] || ""}</span>
    <span className="conn-tile-act"><span className="conn-pill conn-pill-accent">Connect</span></span>
  </button>
);

// The one call to action: a menu of everything this instance can connect, the
// three kinds as sections with their logos, a search box, and each kind's
// escape hatch at its end (your own OAuth service, any key, any MCP URL).
const AddConnectionMenu = ({ providers, presets, onPick, onAddOwn }) => {
  const { open, setOpen, ref } = useDropdown();
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const match = (p) => !q || (p.label || p.name).toLowerCase().includes(q) || p.name.toLowerCase().includes(q);
  const accounts = providers.filter((p) => p.kind === "oauth").filter(match);
  const credentials = providers.filter((p) => p.kind === "static" && p.name !== "generic").filter(match);
  const generic = providers.find((p) => p.name === "generic");
  const mcpProvider = providers.find((p) => p.kind === "mcp") || null;
  const servers = presets.filter(match);
  const toggle = () => {
    setQuery("");
    setOpen(!open);
  };
  const choose = (p) => {
    setOpen(false);
    setQuery("");
    onPick(p);
  };
  const section = (title, items, foot) => (
    <div className="conn-add-section">
      <div className="conn-add-title">{title}</div>
      {items.length > 0 && (
        <div className="conn-add-grid">
          {items.map((p) => (
            <button key={p.name} type="button" role="menuitem" className="conn-add-item" onClick={() => choose(p.kind === "mcp" || p.url ? { ...mcpProvider, preset: p } : p)}>
              <ConnIcon url={p.icon_url} size={18} />
              <span>{p.label}</span>
              {(p.custom || p.personal) && <span className="conn-menu-badge">yours</span>}
            </button>
          ))}
        </div>
      )}
      {foot}
    </div>
  );
  const hatch = (label, onClick) => (
    <button type="button" role="menuitem" className="conn-add-item conn-add-hatch" onClick={onClick}>
      <PlusIcon /> {label}
    </button>
  );
  return (
    <div className="menu conn-add" ref={ref}>
      <button type="button" className="btn btn-primary" onClick={toggle} aria-haspopup="menu" aria-expanded={open}>
        <PlusIcon /> Add connection <span className="conn-caret" aria-hidden="true">&#9662;</span>
      </button>
      {open && (
        <div className="menu-items conn-add-menu" role="menu">
          <input type="text" className="conn-menu-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search services..." aria-label="Search providers" autoFocus />
          {section("Accounts (sign in)", accounts, hatch("Add your own OAuth service", () => { setOpen(false); onAddOwn(); }))}
          <div className="conn-menu-divider" role="separator" />
          {section("Credentials (paste a key)", credentials, generic && hatch("Other API key or token", () => choose(generic)))}
          {mcpProvider && (
            <>
              <div className="conn-menu-divider" role="separator" />
              {section("MCP servers (tools)", servers.map((s) => ({ ...s, kind: "mcp" })), hatch("Any MCP server by URL", () => choose({ ...mcpProvider, preset: null })))}
            </>
          )}
          {q && accounts.length + credentials.length + servers.length === 0 && <p className="conn-menu-none">Nothing matches &ldquo;{query}&rdquo;</p>}
        </div>
      )}
    </div>
  );
};

// The tools button on an MCP tile (the endpoint is in the meta line).
// The tool list. Search appears only once there are enough tools to need it.
const toolsSearchThreshold = 8;

// The MCP Tools view switch: the detailed name+description list, or compact name
// chips (the same chip look as the scopes above). Mirrors the dashboard's
// cards/list toggle, and reuses its pill styling.
const ToolViewToggle = ({ view, onChange }) => (
  <div className="dash-viewtoggle manage-tools-toggle" role="group" aria-label="How to show tools">
    <button
      type="button"
      className={"dash-viewbtn" + (view === "list" ? " on" : "")}
      onClick={() => onChange("list")}
      title="Detailed list"
      aria-label="Detailed list"
      aria-pressed={view === "list"}
    >
      <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <rect x="1" y="3" width="14" height="2" rx="1" />
        <rect x="1" y="7" width="14" height="2" rx="1" />
        <rect x="1" y="11" width="14" height="2" rx="1" />
      </svg>
    </button>
    <button
      type="button"
      className={"dash-viewbtn" + (view === "chips" ? " on" : "")}
      onClick={() => onChange("chips")}
      title="Name chips"
      aria-label="Name chips"
      aria-pressed={view === "chips"}
    >
      <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <rect x="1" y="4" width="6" height="3.4" rx="1.7" />
        <rect x="9" y="4" width="6" height="3.4" rx="1.7" />
        <rect x="1" y="9" width="6" height="3.4" rx="1.7" />
        <rect x="9" y="9" width="6" height="3.4" rx="1.7" />
      </svg>
    </button>
  </div>
);

// Permission toggles: one switch per scope option, the same design the manage
// and add dialogs share.
const ScopeSwitches = ({ options, selected, onToggle, disabled }) => (
  <div className="manage-switch-list">
    {options.map((o) => {
      const on = selected.includes(o.key);
      return (
        <div key={o.key} className="manage-switch-row">
          <span className="manage-switch-text">{o.label}{o.help && <small>{o.help}</small>}</span>
          <button type="button" role="switch" aria-checked={on} className={"conn-switch" + (on ? " on" : "")} onClick={() => onToggle(o.key, !on)} aria-label={o.label} disabled={disabled}><span className="conn-switch-knob" /></button>
        </div>
      );
    })}
  </div>
);

// The one place to manage a connection: its details, the include-by-default
// toggle, a health check, reconnect/rename/remove, and -- for an MCP or a
// delegated server -- the tools it offers. A managed (operator) connection
// shows who provides it and cannot be renamed or removed here.
const ManageConnectionModal = ({ conn, provider, onChanged, onClose, onRemove }) => {
  const managed = conn.managed;
  const dgLocked = conn.default_grant_locked; // auto-grant: forced on by the operator, not the user's to change
  const delegated = conn.kind === "delegated";
  const tools = conn.tools || [];
  // A pasted credential can be replaced here; an OAuth account's read scopes
  // can be changed (which re-authorizes). The connection carries its own
  // scope-options, so it stays editable even after dropping out of the offered
  // list.
  const editableFields = conn.kind === "static" ? provider?.fields || [] : [];
  const scopeOptions = conn.scope_options || provider?.scope_options || [];
  const originalScopeKeys = conn.scope_keys || [];

  const [dg, setDg] = useState(!!conn.default_grant);
  const [label, setLabel] = useState(conn.label || "");
  const [slug, setSlug] = useState(conn.slug);
  const [values, setValues] = useState({});
  const [scopeKeys, setScopeKeys] = useState(originalScopeKeys);
  const [status, setStatus] = useState(conn.status);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [toolQuery, setToolQuery] = useState("");
  const [toolView, setToolView] = useState("list");

  const toggleScope = (key, on) => setScopeKeys((keys) => (on ? [...keys, key] : keys.filter((k) => k !== key)));
  const scopesChanged = !sameScopeKeys(scopeKeys, originalScopeKeys);
  const touched = Object.values(values).some((v) => (v || "").trim() !== "");
  const incomplete = touched && editableFields.some((f) => !f.optional && !(values[f.name] || "").trim());
  const editChanged = slug.trim().toLowerCase() !== conn.slug || label.trim() !== (conn.label || "") || touched || scopesChanged || (dg !== !!conn.default_grant);
  const canSave = !managed && editChanged && !incomplete && slug.trim().length >= 3;

  const warn = status === "needs_reconnect" || status === "needs_admin" || status === "no_access" || status === "unreachable";
  const statusText = { ok: "Connected", needs_reconnect: "Needs reconnect", needs_admin: "Needs administrator", no_access: "No access yet", unreachable: "Not responding" }[status] || "Connected";
  const shownTools = filterTools(tools, toolQuery);

  // A managed connection's only lever is the default toggle, and it applies at
  // once (there is no Save). For everything else the toggle is part of the form
  // and applies on Save with the rest.
  const onToggle = async () => {
    if (dgLocked) return; // the operator owns this one
    const next = !dg;
    setDg(next);
    if (!managed) return;
    setError("");
    try {
      await api.put(`/api/connections/${encodeURIComponent(conn.slug)}`, { default_grant: next });
      onChanged();
    } catch (err) {
      setDg(!next);
      setError(err.message);
    }
  };

  const check = async () => {
    setBusy("check");
    setError("");
    try {
      const res = await api.post(`/api/connections/${encodeURIComponent(conn.slug)}/verify`, {});
      setStatus(res.status || "ok");
      onChanged();
    } catch (err) {
      // A delegated service that will not answer now reads unreachable; the
      // error is shown below and the card behind updates.
      if (delegated) {
        setStatus("unreachable");
        onChanged();
      }
      setError(err.message);
    } finally {
      setBusy("");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setBusy("save");
    setError("");
    const nextSlug = slug.trim().toLowerCase();
    try {
      const metaChanged = nextSlug !== conn.slug || label.trim() !== (conn.label || "") || touched || dg !== !!conn.default_grant;
      let targetSlug = conn.slug;
      if (metaChanged) {
        const body = { slug: nextSlug, label: label.trim(), default_grant: dg };
        if (touched) body.values = values;
        await api.put(`/api/connections/${encodeURIComponent(conn.slug)}`, body);
        targetSlug = nextSlug;
      }
      // Changing the granted scopes re-authorizes with the provider, which
      // leaves the page; the PUT above runs first so it targets the new slug.
      if (scopesChanged) {
        const res = await api.post(`/api/connections/${encodeURIComponent(targetSlug)}/reconnect`, { scope_keys: scopeKeys });
        window.location.href = res.redirect_url;
        return;
      }
      onChanged();
      onClose();
    } catch (err) {
      setError(err.message);
      setBusy("");
    }
  };

  // Reconnect stays in the modal: an OAuth account leaves for its provider (a
  // redirect), an MCP server just re-authorizes; either way an error shows here
  // under the status rather than closing the dialog.
  const reconnect = async () => {
    setBusy("reconnect");
    setError("");
    try {
      const res = await api.post(`/api/connections/${encodeURIComponent(conn.slug)}/reconnect`, {});
      if (res && res.redirect_url) {
        window.location.href = res.redirect_url;
        return;
      }
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy("");
    }
  };

  return (
    <Modal wide onClose={onClose} title={conn.label || conn.slug}>
      <div className="manage-head">
        <span className="manage-logo"><ConnIcon url={conn.icon_url} size={40} /></span>
        <div className="manage-headtext">
          <div className="manage-type">
            {(conn.url ? "MCP server" : kindNames[conn.kind]) || ""}
            {managed && <span className="manage-provided"> (Provided by administrator)</span>}
          </div>
          <div className="manage-statusline"><span className={"conn-tile-dot" + dotClass(status)} aria-hidden="true" /> {statusText}</div>
          {error && <p className="manage-error" role="alert">{error}</p>}
        </div>
      </div>

      <form onSubmit={save}>
        {!managed && (
          <>
            <div className="manage-block">
              <label className="conn-field">
                <span>Name</span>
                <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} aria-label="Name" disabled={busy === "save"} />
              </label>
              <label className="conn-field">
                <span>
                  Reference
                  {slug !== conn.slug && (
                    <span className="field-warn" title="Apps ask for this connection by its reference">
                      {" "}<WarnIcon /> Changing this may break apps
                    </span>
                  )}
                </span>
                <input type="text" value={slug} onChange={(e) => setSlug(filterSlugInput(e.target.value))} aria-label="Reference" maxLength={32} disabled={busy === "save"} />
              </label>
            </div>
            {scopeOptions.length > 0 && (
              <div className="manage-block">
                <span className="manage-label">Permissions</span>
                <ScopeSwitches options={scopeOptions} selected={scopeKeys} onToggle={toggleScope} disabled={busy === "save"} />
              </div>
            )}
            {editableFields.length > 0 && (
              <div className="manage-block">
                <span className="manage-label">Credential</span>
                {editableFields.map((f) => (
                  <label key={f.name} className="conn-field">
                    <span>{f.label}{f.optional ? " (optional)" : ""}</span>
                    {f.multiline ? (
                      <textarea rows={7} className="mono conn-textarea" value={values[f.name] || ""} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })} placeholder={f.secret ? "unchanged" : f.placeholder} aria-label={f.label} disabled={busy === "save"} spellCheck={false} />
                    ) : (
                      <input type={f.secret ? "password" : "text"} value={values[f.name] || ""} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })} placeholder={f.secret ? "unchanged" : f.placeholder} aria-label={f.label} disabled={busy === "save"} />
                    )}
                  </label>
                ))}
                {incomplete && (
                  <p className="hint conn-warn">
                    Replacing the credential replaces all of it, so fill in every field above or clear
                    them to leave it alone.
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {delegated && (conn.available_scopes || []).length > 0 && (
          <div className="manage-block">
            <span className="manage-label">Granted Scopes</span>
            <div className="conn-tile-scopes">
              {conn.available_scopes.map((sc) => <span key={sc} className="conn-scope-chip">{sc}</span>)}
            </div>
          </div>
        )}

        {tools.length > 0 && (
          <div className="manage-block">
            <div className="manage-tools-head">
              <span className="manage-label">MCP Tools</span>
              <ToolViewToggle view={toolView} onChange={setToolView} />
            </div>
            {tools.length >= toolsSearchThreshold && (
              <input type="text" className="conn-menu-search conn-tools-search" value={toolQuery} onChange={(e) => setToolQuery(e.target.value)} placeholder="Search tools..." aria-label="Search tools" />
            )}
            {toolView === "chips" ? (
              <div className="conn-tile-scopes manage-tools-chips">
                {shownTools.map((t) => (
                  <span key={t.name} className="conn-scope-chip" title={t.description || undefined}>{t.name}</span>
                ))}
              </div>
            ) : (
              <ul className="manage-tools-list">
                {shownTools.map((t) => (
                  <li key={t.name}><b>{t.name}</b>{t.description ? `: ${t.description}` : ""}</li>
                ))}
              </ul>
            )}
            {shownTools.length === 0 && <p className="hint">Nothing matches &ldquo;{toolQuery}&rdquo;</p>}
          </div>
        )}

        <div className="manage-block">
          <span className="manage-label">Availability</span>
          <div className="manage-switch-list">
            <div className="manage-switch-row">
              <span className="manage-switch-text">Include by default in all apps and chats<small>{dgLocked ? "Your administrator grants this to all your apps and chats." : "Granted to every new chat and app you create."}</small></span>
              <button type="button" role="switch" aria-checked={dgLocked ? true : dg} className={"conn-switch" + ((dgLocked || dg) ? " on" : "")} onClick={onToggle} aria-label="Include by default in all apps and chats" disabled={dgLocked}><span className="conn-switch-knob" /></button>
            </div>
          </div>
        </div>

        <div className="btn-row manage-actions">
          {(conn.kind === "oauth" || delegated) && (
            <button type="button" className="btn" onClick={check} disabled={busy === "check"}>
              {busy === "check" ? "Checking..." : delegated ? "Refresh now" : "Check health"}
            </button>
          )}
          {(conn.kind === "oauth" || conn.kind === "mcp") && !managed && (
            <button type="button" className="btn" onClick={reconnect} disabled={busy === "reconnect"}>{busy === "reconnect" ? "Reconnecting..." : "Reconnect"}</button>
          )}
          {!managed && <button type="button" className="btn btn-danger" onClick={() => { onClose(); onRemove(conn); }}>Remove</button>}
          <span className="manage-spacer" />
          {!managed && (
            <button type="submit" className="btn btn-primary" disabled={!canSave || busy === "save"}>
              {busy === "save" ? "Saving..." : "Save"}
            </button>
          )}
          {managed && <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>}
        </div>
      </form>
    </Modal>
  );
};

const PlusIcon = () => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
    <path d="M8 3.5v9M3.5 8h9" />
  </svg>
);

// RequiredStar marks a mandatory field.
const RequiredStar = () => (
  <span className="req" title="Required" aria-label="required">*</span>
);

// WarnIcon is a small triangle-with-bang, inline in a field label.
const WarnIcon = () => (
  <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ verticalAlign: "-1px" }}>
    <path d="M8 2.2 14.5 13.3H1.5L8 2.2Z" />
    <path d="M8 6.4v3" />
    <path d="M8 11.4h.01" />
  </svg>
);

// The one place a connection is created, for both kinds. A pasted credential
// saves here; an OAuth account leaves for the provider's consent screen, which
// is why the button says where it is going.
//
// Two fields, deliberately distinct: the NAME is for the person, free text and
// changeable; the REFERENCE is what an app asks for, and is derived from the
// name so nobody has to invent both.
const AddConnectionDialog = ({ provider, existing, onClose, onAdded }) => {
  const preset = provider.preset || null;
  const [label, setLabel] = useState(preset?.label || "");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [values, setValues] = useState(preset ? { url: preset.url } : {});
  const scopeOptions = provider.scope_options || [];
  const [scopeKeys, setScopeKeys] = useState(() => defaultScopeKeys(provider));
  const toggleScope = (key, on) => setScopeKeys((keys) => (on ? [...keys, key] : keys.filter((k) => k !== key)));
  const [defaultGrant, setDefaultGrant] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const oauth = provider.kind === "oauth";
  const mcp = provider.kind === "mcp";

  // Naming is optional: an empty field uses the placeholder the user saw.
  const effectiveLabel = connectionLabel(label, provider);
  // Until the reference is edited by hand it follows the name; once touched it
  // stays put, because renaming a connection must not silently move what apps
  // ask for.
  const reference = slugEdited ? slug : slugify(effectiveLabel) || suggestSlug(provider, existing);
  const missing = (provider.fields || []).some((f) => !f.optional && !(values[f.name] || "").trim());
  const valid = reference.length >= 3 && !missing;

  const submit = async (e) => {
    e.preventDefault();
    if (busy || !valid) return;
    setBusy(true);
    setError("");
    try {
      const body = { provider: provider.name, slug: reference, label: effectiveLabel, values, default_grant: defaultGrant };
      if (scopeOptions.length > 0) {
        body.scope_keys = scopeKeys;
      }
      const res = await api.post("/api/connections", body);
      if (res.redirect_url) {
        window.location.href = res.redirect_url;
        return;
      }
      onAdded();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title={`${oauth ? "Connect" : "Add"} ${provider.label}`} help={provider.help}>
      <form onSubmit={submit}>
        <ErrorBanner message={error} onDismiss={() => setError("")} />
        {provider.short_description && <p className="hint">{provider.short_description}</p>}
        <label className="conn-field">
          <span>Name</span>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={provider.name_hint || provider.label}
            aria-label="Name"
            autoFocus
            disabled={busy}
          />
        </label>
        <label className="conn-field">
          <span>Reference</span>
          <input
            type="text"
            value={reference}
            onChange={(e) => {
              setSlugEdited(true);
              setSlug(filterSlugInput(e.target.value));
            }}
            placeholder="work-calendar"
            aria-label="Reference"
            maxLength={32}
            disabled={busy}
          />
        </label>
        {scopeOptions.length > 0 && (
          <div className="manage-block">
            <span className="manage-label">Permissions</span>
            <ScopeSwitches options={scopeOptions} selected={scopeKeys} onToggle={toggleScope} disabled={busy} />
          </div>
        )}
        {(provider.fields || []).map((f) => (
          <label key={f.name} className="conn-field">
            <span>{f.label}{f.optional ? " (optional)" : ""}</span>
            {f.multiline ? (
              <textarea
                rows={7}
                className="mono conn-textarea"
                value={values[f.name] || ""}
                onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
                placeholder={f.placeholder}
                aria-label={f.label}
                disabled={busy}
                spellCheck={false}
              />
            ) : (
              <input
                type={f.secret ? "password" : "text"}
                value={values[f.name] || ""}
                onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
                placeholder={f.placeholder}
                aria-label={f.label}
                disabled={busy}
              />
            )}
          </label>
        ))}
        <div className="manage-block">
          <span className="manage-label">Availability</span>
          <div className="manage-switch-list">
            <div className="manage-switch-row">
              <span className="manage-switch-text">Include by default in all apps and chats<small>Granted to every new chat and app you create.</small></span>
              <button type="button" role="switch" aria-checked={defaultGrant} className={"conn-switch" + (defaultGrant ? " on" : "")} onClick={() => setDefaultGrant(!defaultGrant)} aria-label="Include by default in all apps and chats" disabled={busy}><span className="conn-switch-knob" /></button>
            </div>
          </div>
        </div>
        <div className="btn-row">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy || !valid}>
            {busy && <span className="newapp-spinner" aria-hidden="true" />}
            {oauth ? `Continue to ${provider.label}` : busy ? mcpBusyLabel(mcp) : mcp ? "Connect" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// An MCP server is contacted while the dialog waits: discovery is a round trip
// to a server that may want authorization, which takes longer than saving a
// pasted key and should not look like a hang.
const mcpBusyLabel = (mcp) => (mcp ? "Contacting server..." : "Saving...");

// The shared modal chrome, so the two dialogs differ only in their fields.
const Modal = ({ title, help, wide, wider, onClose, children }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
  <div className="modal-backdrop" role="dialog" aria-modal="true" onMouseDown={onClose}>
    <div className={"card modal modal-sheet" + (wider ? " modal-wider" : wide ? " modal-tools" : "")} onMouseDown={(e) => e.stopPropagation()}>
      <button type="button" className="modal-x" onClick={onClose} title="Close" aria-label="Close">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <path d="M6 6l12 12M18 6 6 18" />
        </svg>
      </button>
      <h2>{title}</h2>
      {help && <p className="hint" style={{ marginBottom: "5px" }}>{help}</p>}
      {children}
    </div>
  </div>
  );
};

export default Connections;
